import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CheckCircle, XCircle, Loader } from "lucide-react";
import { useB2BTranslation } from "../../B2BShared/hooks/useB2BTranslation";
import { useB2BAuth } from "../../B2BShared/B2BAuthContext/B2BAuthContext";
import "./B2BEmailConfirm.css";

type VerificationStatus = 'loading' | 'success' | 'error';
type ErrorType = 'expired' | 'already_used' | 'network' | 'generic';

const B2BEmailConfirm = () => {
  const { tAuth, i18n } = useB2BTranslation();
  const { token } = useParams();
  const navigate = useNavigate();
  const { verifyEmail, refreshUserData, resendVerification } = useB2BAuth();
  
  const [status, setStatus] = useState<VerificationStatus>('loading');
  const [message, setMessage] = useState('');
  const [errorType, setErrorType] = useState<ErrorType>('generic');
  const [userEmail, setUserEmail] = useState<string>('');
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  
  // Use refs to prevent race conditions
  const verificationAttempted = useRef(false);
  const isComponentMounted = useRef(true);

  // Cleanup function
  const cleanup = useCallback(() => {
    isComponentMounted.current = false;
  }, []);

  // Safe state setters that check if component is still mounted
  const safeSetStatus = useCallback((newStatus: VerificationStatus) => {
    if (isComponentMounted.current) {
      setStatus(newStatus);
    }
  }, []);

  const safeSetMessage = useCallback((newMessage: string) => {
    if (isComponentMounted.current) {
      setMessage(newMessage);
    }
  }, []);

  // Handle successful verification:
  // Instead of showing a standalone success page, jump straight to the questionnaire
  // and show a dismissible confirmation banner there.
  const handleSuccess = useCallback(
    async (verifiedMessage?: string) => {
    // Refresh user data to get latest state (best-effort)
    try {
      await refreshUserData();
    } catch {
      // ignore refresh issues; user can still proceed
    }
      navigate("/gcb/profile/create?verified=1", {
        replace: true,
        state: {
          emailVerified: true,
          verifiedMessage,
        },
      });
    },
    [navigate, refreshUserData]
  );

  // Handle verification error
  const handleError = useCallback((errorMessage: string, type: ErrorType = 'generic', email?: string) => {
    safeSetStatus('error');
    safeSetMessage(errorMessage);
    setErrorType(type);
    if (email) setUserEmail(email);
  }, [safeSetStatus, safeSetMessage]);

  // Main verification logic
  const performEmailVerification = useCallback(async () => {
    // Prevent multiple verification attempts
    if (verificationAttempted.current || !isComponentMounted.current) {
      return;
    }

    if (!token) {
      handleError(tAuth('invalidVerificationLink'));
      return;
    }

    verificationAttempted.current = true;

    try {
      const response = await verifyEmail(token);
      
      if (!isComponentMounted.current) return;

      if (response.success) {
        await handleSuccess(response.message || tAuth("emailVerifiedMessage"));
      } else {
        handleError(response.message || tAuth('verificationError'));
      }
    } catch (error: any) {
      if (!isComponentMounted.current) return;
      
      const data = error.response?.data || {};
      const errorMessage = data.message || error.message || '';
      const code = data.code || '';

      // Already verified — treat as success
      if (errorMessage.includes('already verified') || errorMessage.includes('already been used') || error.response?.status === 409) {
        await handleSuccess(tAuth("emailAlreadyVerified") || "Your email is already verified. You can sign in.");
        return;
      }

      // Token expired — offer to resend
      if (code === 'TOKEN_EXPIRED' || errorMessage.includes('expired')) {
        handleError(
          "Your verification link has expired. Click below to receive a new one.",
          'expired',
          data.email || ''
        );
        return;
      }

      // Token invalid (already used)
      if (code === 'TOKEN_INVALID') {
        handleError(
          "This link has already been used. If you already verified your email, please sign in.",
          'already_used'
        );
        return;
      }

      // Network / server errors
      const msg = (error.message || '').toLowerCase();
      if (msg.includes('network') || msg.includes('timeout') || msg.includes('fetch') || msg.includes('load failed')) {
        handleError(
          "The server is waking up. Please wait a few seconds and try again.",
          'network'
        );
        return;
      }

      // Generic fallback
      handleError(
        "Verification failed. Please try again or contact support.",
        'generic'
      );
    }
  }, [token, verifyEmail, handleSuccess, handleError, tAuth]);

  // Component mount/unmount effects
  useEffect(() => {
    isComponentMounted.current = true;
    return cleanup;
  }, [cleanup]);

  // Language change effect
  useEffect(() => {
    const handleLanguageChange = () => {
      // Language changed - component will re-render automatically
    };

    i18n.on("languageChanged", handleLanguageChange);
    return () => i18n.off("languageChanged", handleLanguageChange);
  }, [i18n]);

  // Main verification effect
  useEffect(() => {
    performEmailVerification();
  }, [performEmailVerification]);

  const getIcon = () => {
    const iconStyle = { marginBottom: '1.5rem', width: '64px', height: '64px' };
    switch (status) {
      case 'loading':
        return <Loader className="spinning verification-icon" size={64} style={{ ...iconStyle, color: '#ea580c' }} />;
      case 'success':
        return <CheckCircle className="verification-icon success" size={64} style={{ ...iconStyle, color: '#059669' }} />;
      case 'error':
        return <XCircle className="verification-icon error" size={64} style={{ ...iconStyle, color: '#dc2626' }} />;
    }
  };

  const getTitle = () => {
    switch (status) {
      case 'loading':
        return tAuth('verifyingEmail');
      case 'success':
        return tAuth('emailVerifiedSuccessfully');
      case 'error':
        return tAuth('emailVerificationFailed');
    }
  };

  return (
    <div className="email-confirm-container" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)', padding: '1rem', fontFamily: 'Inter, sans-serif' }}>
      <div className="email-confirm-content" style={{ background: 'white', borderRadius: '2rem', padding: '4rem', maxWidth: '600px', width: '100%', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', textAlign: 'center', border: '1px solid rgba(255, 255, 255, 0.2)', position: 'relative' }}>
        {/* Top Accent Bar */}
        <div style={{ position: 'absolute', top: '0', left: '0', right: '0', height: '4px', background: 'linear-gradient(90deg, #ea580c, #f97316)', borderRadius: '2rem 2rem 0 0' }}></div>
        
        <div className="email-confirm-header" style={{ marginBottom: '3rem' }}>
          {getIcon()}
          <h1 style={{ fontSize: '2.25rem', fontWeight: '700', color: '#1e293b', marginBottom: '1rem', lineHeight: '1.2' }}>
            {getTitle()}
          </h1>
          <p className={`confirm-message ${status}`} style={{ fontSize: '1.125rem', lineHeight: '1.6', margin: '0' }}>
            {message}
          </p>
        </div>

        {/* Success now redirects to questionnaire; no standalone success page */}

        {status === 'error' && (
          <div className="error-actions" style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '1rem', padding: '2rem', marginBottom: '2rem' }}>
            <div className="action-buttons" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', alignItems: 'center', marginBottom: '1.5rem' }}>

              {/* Expired token → resend verification email */}
              {errorType === 'expired' && (
                <>
                  {resendSuccess ? (
                    <p style={{ color: '#16a34a', fontWeight: '600', fontSize: '1rem', margin: 0 }}>
                      A new verification email has been sent! Please check your inbox.
                    </p>
                  ) : (
                    <button
                      onClick={async () => {
                        if (!userEmail || resending) return;
                        setResending(true);
                        try {
                          await resendVerification(userEmail);
                          setResendSuccess(true);
                        } catch {
                          safeSetMessage("Failed to resend. Please try again or contact support.");
                        } finally {
                          setResending(false);
                        }
                      }}
                      disabled={resending || !userEmail}
                      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '0.75rem 2rem', borderRadius: '0.75rem', fontSize: '1rem', fontWeight: '600', cursor: 'pointer', background: '#ea580c', color: 'white', border: 'none', minWidth: '200px', opacity: resending ? 0.6 : 1 }}
                    >
                      {resending ? 'Sending...' : 'Resend Verification Email'}
                    </button>
                  )}
                </>
              )}

              {/* Already used → sign in */}
              {errorType === 'already_used' && (
                <button
                  onClick={() => navigate("/gcb/login")}
                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '0.75rem 2rem', borderRadius: '0.75rem', fontSize: '1rem', fontWeight: '600', cursor: 'pointer', background: '#ea580c', color: 'white', border: 'none', minWidth: '200px' }}
                >
                  Sign In
                </button>
              )}

              {/* Network error → retry */}
              {errorType === 'network' && (
                <button
                  onClick={() => window.location.reload()}
                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '0.75rem 2rem', borderRadius: '0.75rem', fontSize: '1rem', fontWeight: '600', cursor: 'pointer', background: '#ea580c', color: 'white', border: 'none', minWidth: '200px' }}
                >
                  Try Again
                </button>
              )}

              {/* Generic error → sign in */}
              {errorType === 'generic' && (
                <button
                  onClick={() => navigate("/gcb/login")}
                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '0.75rem 2rem', borderRadius: '0.75rem', fontSize: '1rem', fontWeight: '600', cursor: 'pointer', background: '#ea580c', color: 'white', border: 'none', minWidth: '200px' }}
                >
                  Sign In
                </button>
              )}
            </div>
            <div className="help-section" style={{ textAlign: 'center', paddingTop: '1.5rem', borderTop: '1px solid #fecaca' }}>
              <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '0', lineHeight: '1.5' }}>
                Need help? <a href="mailto:info@globalchic.club" style={{ color: '#ea580c', textDecoration: 'none', fontWeight: '500' }}>Contact support</a>
              </p>
            </div>
          </div>
        )}

        {status === 'loading' && (
          <div className="loading-info">
            <p>{tAuth('pleaseWaitVerifying')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default B2BEmailConfirm;